import { useCallback, useEffect, useState } from 'react';
import { useOutletContext } from 'react-router-dom';

import { ProfileUsers } from '../../../../../../api/enumerations';
import { getInAccreditation } from '../../../../../../api/theHive/redeProto';
import { InAccreditationItem } from '../../../../../../api/theHive/redeProto/types';
import { useCurrentRole } from '../../../../../../hooks/useCurrentRole';
import useErrorMessage from '../../../../../../hooks/useErrorMessage';
import useSnackbar from '../../../../../../hooks/useSnackbar';
import { RedeProtoOutletContext } from '../../types';

const PER_PAGE = 100;

export type InAccreditationOrder = 'fifo' | 'recent';

interface UseInAccreditationHook {
  role: number | null;
  isAllowed: boolean;
  items: InAccreditationItem[];
  page: number;
  setPage: (page: number) => void;
  totalPages: number;
  loading: boolean;
  expanded: number[];
  toggleExpand: (id: number) => void;
  order: InAccreditationOrder;
  setOrder: (order: InAccreditationOrder) => void;
}

export default function useInAccreditation(): UseInAccreditationHook {
  const role = useCurrentRole();
  const { selectedNoticeId } = useOutletContext<RedeProtoOutletContext>();

  const [items, setItems] = useState<InAccreditationItem[]>([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState<number[]>([]);
  const [order, setOrderState] = useState<InAccreditationOrder>('fifo');

  const { getErrorMessage } = useErrorMessage();
  const { handleSnackbar } = useSnackbar();

  const isAllowed =
    role === ProfileUsers.ADMIN || role === ProfileUsers.BANK_ADMIN;

  const toggleExpand = useCallback((id: number): void => {
    setExpanded((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
    );
  }, []);

  // Trocar a ordem volta para a 1ª página (a lista muda de topo).
  const setOrder = useCallback((next: InAccreditationOrder): void => {
    setOrderState(next);
    setPage(1);
  }, []);

  const fetchData = useCallback(async (): Promise<void> => {
    setLoading(true);
    try {
      const response = await getInAccreditation(
        selectedNoticeId,
        page,
        PER_PAGE,
        order
      );
      if (response.detail.description) {
        throw new Error(response.detail.description);
      }
      setItems(response.data ?? []);
      setTotalPages(response.detail.total_pages ?? 1);
    } catch (error) {
      handleSnackbar(getErrorMessage(error), true);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, selectedNoticeId, order]);

  useEffect(() => {
    if (isAllowed) fetchData();
  }, [fetchData, isAllowed]);

  return {
    role,
    isAllowed,
    items,
    page,
    setPage,
    totalPages,
    loading,
    expanded,
    toggleExpand,
    order,
    setOrder,
  };
}
